/**
 * Tiny inline-SVG chart primitives. No charting lib.
 *
 * These exist to answer the pilot's central question on the admin overview
 * page: "how is usage relating to performance, who's using the app, and
 * where are students pointing the format filter?"
 *
 * Three chart types:
 *   <ScatterChart>          — usage (attempts) vs. accuracy (%) per student
 *   <HistogramChart>        — distribution of activity across students
 *   <HorizontalBarChart>    — format demand counts (selections / served)
 *
 * They're designed to be readable at admin-dashboard sizes (~600×280) and
 * to degrade to a single column on mobile (CSS handles the wrapping; the
 * SVGs use viewBox + preserveAspectRatio so they scale).
 */
import { useState } from "react";

const FG = "#0a0a0a";
const FG_MUTED = "#9c9ca3";
const LINE = "#e6e6e9";
const POS = "#1f7a3a";
const NEG = "#b3261e";

// ---------------------------------------------------------------------------
// ScatterChart — usage vs. accuracy
// ---------------------------------------------------------------------------

export interface ScatterPoint {
  x: number;       // attempts
  y: number;       // % correct (0-1)
  label: string;   // blazerid for hover
}

export function ScatterChart({
  points,
  xLabel,
  yLabel,
  width = 600,
  height = 280,
}: {
  points: ScatterPoint[];
  xLabel: string;
  yLabel: string;
  width?: number;
  height?: number;
}) {
  const M = { t: 18, r: 22, b: 38, l: 42 };
  const innerW = width - M.l - M.r;
  const innerH = height - M.t - M.b;
  const [hover, setHover] = useState<ScatterPoint | null>(null);

  if (points.length === 0) {
    return <EmptyChart width={width} height={height} message="No data yet." />;
  }

  const maxX = Math.max(1, ...points.map((p) => p.x));
  const xTicks = niceTicks(0, maxX, 5);
  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  const sx = (x: number) => M.l + (x / Math.max(...xTicks)) * innerW;
  const sy = (y: number) => M.t + (1 - y) * innerH;

  return (
    <div className="chart-wrap">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`${yLabel} vs. ${xLabel}`}
        className="chart-svg"
      >
        {/* Axes */}
        <line x1={M.l} y1={M.t} x2={M.l} y2={M.t + innerH} stroke={LINE} />
        <line
          x1={M.l}
          y1={M.t + innerH}
          x2={M.l + innerW}
          y2={M.t + innerH}
          stroke={LINE}
        />

        {/* Y gridlines + labels */}
        {yTicks.map((y, i) => (
          <g key={i}>
            <line
              x1={M.l}
              y1={sy(y)}
              x2={M.l + innerW}
              y2={sy(y)}
              stroke={LINE}
              strokeDasharray="2 4"
            />
            <text x={M.l - 8} y={sy(y) + 4} textAnchor="end" fontSize="11" fill={FG_MUTED}>
              {Math.round(y * 100)}%
            </text>
          </g>
        ))}

        {/* X labels */}
        {xTicks.map((x, i) => (
          <g key={i}>
            <text
              x={sx(x)}
              y={M.t + innerH + 16}
              textAnchor="middle"
              fontSize="11"
              fill={FG_MUTED}
            >
              {x}
            </text>
          </g>
        ))}

        {/* Axis titles */}
        <text
          x={M.l + innerW / 2}
          y={height - 6}
          textAnchor="middle"
          fontSize="11"
          fill={FG_MUTED}
        >
          {xLabel}
        </text>
        <text
          x={-(M.t + innerH / 2)}
          y={14}
          transform="rotate(-90)"
          textAnchor="middle"
          fontSize="11"
          fill={FG_MUTED}
        >
          {yLabel}
        </text>

        {/* Points */}
        {points.map((p, i) => (
          <circle
            key={`${p.label}-${i}`}
            cx={sx(p.x)}
            cy={sy(p.y)}
            r={hover === p ? 7 : 5}
            fill={p.y >= 0.7 ? POS : p.y >= 0.4 ? FG : NEG}
            opacity={hover && hover !== p ? 0.35 : 0.85}
            onMouseEnter={() => setHover(p)}
            onMouseLeave={() => setHover(null)}
            style={{ cursor: "pointer", transition: "r 0.1s ease, opacity 0.1s ease" }}
          />
        ))}

        {hover && (
          <g pointerEvents="none">
            <rect
              x={Math.min(sx(hover.x) + 8, width - 160)}
              y={Math.max(sy(hover.y) - 36, 4)}
              width={150}
              height={32}
              rx={6}
              fill={FG}
            />
            <text
              x={Math.min(sx(hover.x) + 16, width - 152)}
              y={Math.max(sy(hover.y) - 18, 22)}
              fontSize="12"
              fill="#fff"
              fontFamily="var(--font-mono)"
            >
              {hover.label}
            </text>
            <text
              x={Math.min(sx(hover.x) + 16, width - 152)}
              y={Math.max(sy(hover.y) - 4, 36)}
              fontSize="11"
              fill="#fff"
              opacity="0.7"
            >
              {hover.x} attempts · {Math.round(hover.y * 100)}%
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HistogramChart — distribution of values across labeled buckets
// ---------------------------------------------------------------------------

export function HistogramChart({
  buckets,
  width = 600,
  height = 240,
  yLabel,
}: {
  buckets: { label: string; count: number }[];
  width?: number;
  height?: number;
  yLabel?: string;
}) {
  const M = { t: 18, r: 22, b: 48, l: 42 };
  const innerW = width - M.l - M.r;
  const innerH = height - M.t - M.b;

  const maxCount = Math.max(1, ...buckets.map((b) => b.count));
  const yTicks = niceTicks(0, maxCount, 4);
  const yMax = Math.max(...yTicks);

  const barWidth = (innerW / buckets.length) * 0.66;
  const colWidth = innerW / buckets.length;

  if (buckets.length === 0) {
    return <EmptyChart width={width} height={height} message="No data yet." />;
  }

  return (
    <div className="chart-wrap">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        className="chart-svg"
      >
        <line x1={M.l} y1={M.t} x2={M.l} y2={M.t + innerH} stroke={LINE} />
        <line
          x1={M.l}
          y1={M.t + innerH}
          x2={M.l + innerW}
          y2={M.t + innerH}
          stroke={LINE}
        />

        {yTicks.map((y, i) => (
          <g key={i}>
            <line
              x1={M.l}
              y1={M.t + innerH - (y / yMax) * innerH}
              x2={M.l + innerW}
              y2={M.t + innerH - (y / yMax) * innerH}
              stroke={LINE}
              strokeDasharray="2 4"
            />
            <text
              x={M.l - 8}
              y={M.t + innerH - (y / yMax) * innerH + 4}
              textAnchor="end"
              fontSize="11"
              fill={FG_MUTED}
            >
              {y}
            </text>
          </g>
        ))}

        {buckets.map((b, i) => {
          const x = M.l + i * colWidth + (colWidth - barWidth) / 2;
          const h = (b.count / yMax) * innerH;
          const y = M.t + innerH - h;
          return (
            <g key={b.label}>
              <rect x={x} y={y} width={barWidth} height={h} fill={FG} rx={3} />
              <text
                x={x + barWidth / 2}
                y={y - 4}
                textAnchor="middle"
                fontSize="11"
                fill={FG}
                fontFamily="var(--font-mono)"
              >
                {b.count || ""}
              </text>
              <text
                x={x + barWidth / 2}
                y={M.t + innerH + 18}
                textAnchor="middle"
                fontSize="11"
                fill={FG_MUTED}
              >
                {b.label}
              </text>
            </g>
          );
        })}

        {yLabel && (
          <text
            x={-(M.t + innerH / 2)}
            y={14}
            transform="rotate(-90)"
            textAnchor="middle"
            fontSize="11"
            fill={FG_MUTED}
          >
            {yLabel}
          </text>
        )}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HorizontalBarChart — labeled rows with one or two values per row
// ---------------------------------------------------------------------------

export interface HBarRow {
  label: string;
  a: number;
  b?: number;
  aLabel?: string;
  bLabel?: string;
}

export function HorizontalBarChart({
  rows,
  width = 600,
  rowHeight = 44,
  topPad = 16,
  bottomPad = 24,
}: {
  rows: HBarRow[];
  width?: number;
  rowHeight?: number;
  topPad?: number;
  bottomPad?: number;
}) {
  if (rows.length === 0) {
    return <EmptyChart width={width} height={120} message="No data yet." />;
  }
  const height = topPad + bottomPad + rows.length * rowHeight;
  const labelW = 160;
  const barX = labelW + 16;
  const barW = width - barX - 80;
  const maxAB = Math.max(1, ...rows.flatMap((r) => [r.a, r.b ?? 0]));

  return (
    <div className="chart-wrap">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        className="chart-svg"
      >
        {rows.map((r, i) => {
          const yCenter = topPad + i * rowHeight + rowHeight / 2;
          const aW = (r.a / maxAB) * barW;
          const bW = r.b !== undefined ? (r.b / maxAB) * barW : 0;
          return (
            <g key={r.label}>
              <text x={labelW} y={yCenter + 5} textAnchor="end" fontSize="13" fill={FG}>
                {r.label}
              </text>
              {/* primary bar (a) */}
              <rect x={barX} y={yCenter - 14} width={aW} height={10} fill={FG} rx={3} />
              <text
                x={barX + aW + 6}
                y={yCenter - 5}
                fontSize="11"
                fontFamily="var(--font-mono)"
                fill={FG_MUTED}
              >
                {r.aLabel ? `${r.a} ${r.aLabel}` : r.a}
              </text>
              {r.b !== undefined && (
                <>
                  <rect
                    x={barX}
                    y={yCenter + 4}
                    width={bW}
                    height={10}
                    fill={FG_MUTED}
                    rx={3}
                  />
                  <text
                    x={barX + bW + 6}
                    y={yCenter + 13}
                    fontSize="11"
                    fontFamily="var(--font-mono)"
                    fill={FG_MUTED}
                  >
                    {r.bLabel ? `${r.b} ${r.bLabel}` : r.b}
                  </text>
                </>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function EmptyChart({
  width,
  height,
  message,
}: {
  width: number;
  height: number;
  message: string;
}) {
  return (
    <div className="chart-wrap chart-wrap--empty">
      <svg viewBox={`0 0 ${width} ${height}`}>
        <text
          x={width / 2}
          y={height / 2}
          textAnchor="middle"
          fontSize="13"
          fill={FG_MUTED}
        >
          {message}
        </text>
      </svg>
    </div>
  );
}

function niceTicks(min: number, max: number, count: number): number[] {
  const range = max - min;
  if (range === 0) return [min];
  const roughStep = range / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / magnitude;
  let step: number;
  if (normalized < 1.5) step = 1;
  else if (normalized < 3) step = 2;
  else if (normalized < 7) step = 5;
  else step = 10;
  step *= magnitude;
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= niceMax; v += step) ticks.push(Math.round(v * 1e9) / 1e9);
  return ticks;
}
